
import { useEffect, useRef, useCallback } from 'react'

interface UseInactivityTimeoutOptions {
  timeout: number // timeout in milliseconds
  onTimeout: () => void
  isActive: boolean // whether the timeout should be active
}

export function useInactivityTimeout({ timeout, onTimeout, isActive }: UseInactivityTimeoutOptions) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastActivityRef = useRef<number>(Date.now())

  // Reset the timeout
  const resetTimeout = useCallback(() => {
    if (!isActive) return

    lastActivityRef.current = Date.now()

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      console.log('[InactivityTimeout] Session expired due to inactivity')
      onTimeout()
    }, timeout)
  }, [timeout, onTimeout, isActive])

  // Clear the timeout
  const clearInactivityTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  // Activity event handler
  const handleActivity = useCallback(() => {
    if (isActive) {
      resetTimeout()
    }
  }, [resetTimeout, isActive])

  useEffect(() => {
    if (!isActive) {
      clearInactivityTimeout()
      return
    }

    // Events to track for user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ]

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true })
    })

    // Start the timeout
    resetTimeout()

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity)
      })
      clearInactivityTimeout()
    }
  }, [handleActivity, resetTimeout, clearInactivityTimeout, isActive])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return {
    resetTimeout,
    clearInactivityTimeout,
    getLastActivity: () => lastActivityRef.current
  }
}
